import * as d3 from "d3";
import { get, map } from "lodash";
import moment from "moment";
import Cookies from "universal-cookie";
import CryptoJS from "crypto-js";
import { useDispatch } from "react-redux";
import { useRef, useEffect } from "react";
import {
  hideOffCanvas,
  hideOffCanvasLast,
  hideOffCanvasThree,
  setCommonAlert,
} from "../commonSlice.js";
import { convertColorFormat } from "../common-components/ECharts/Helpers.js";
import { DateTime } from "luxon";
import { serverZone } from "./dateUtils.js";

const cookies = new Cookies();

export function formatDate(dateString) {
  const [month, year] = dateString.split("/");
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const monthName = monthNames[parseInt(month, 10) - 1];
  return `${monthName} ${year}`;
}
export const getMonthNumber = (month) => {
  const monthOptions = [
    { name: "Jan", value: "01" },
    { name: "Feb", value: "02" },
    { name: "Mar", value: "03" },
    { name: "Apr", value: "04" },
    { name: "May", value: "05" },
    { name: "Jun", value: "06" },
    { name: "Jul", value: "07" },
    { name: "Aug", value: "08" },
    { name: "Sep", value: "09" },
    { name: "Oct", value: "10" },
    { name: "Nov", value: "11" },
    { name: "Dec", value: "12" },
    { name: "January", value: "01" },
    { name: "February", value: "02" },
    { name: "March", value: "03" },
    { name: "April", value: "04" },
    { name: "June", value: "06" },
    { name: "July", value: "07" },
    { name: "August", value: "08" },
    { name: "September", value: "09" },
    { name: "October", value: "10" },
    { name: "November", value: "11" },
    { name: "December", value: "12" },
  ];
  const selectedMonthObj = monthOptions.find((option) => option.name === month);
  return selectedMonthObj.value;
};

//$ convertion
export const formatAsDollar = (value) => {
  // Use Intl.NumberFormat for number formatting
  const formattedValue = new Intl.NumberFormat("en-SG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

  return `S$${formattedValue}`;
};

export function generateMonthArray(firstMonth, lastMonth) {
  // month array finder
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  let [startMonth, startYear] = firstMonth.split(" ");
  let [endMonth, endYear] = lastMonth.split(" ");

  startYear = parseInt(startYear, 10);
  endYear = parseInt(endYear, 10);

  let startMonthIndex = monthNames.indexOf(startMonth);
  let endMonthIndex = monthNames.indexOf(endMonth);

  let monthsArray = [];

  while (
    startYear < endYear ||
    (startYear === endYear && startMonthIndex <= endMonthIndex)
  ) {
    monthsArray.push(`${monthNames[startMonthIndex]} ${startYear}`);

    startMonthIndex++;

    if (startMonthIndex === 12) {
      startMonthIndex = 0;
      startYear++;
    }
  }

  return monthsArray;
}

export const formatDateSummary = () => {
  const date = new Date();
  date.setMonth(date.getMonth()); // Set to current month
  const options = { year: "2-digit", month: "short" };

  // Get the locale date string and replace the space with a hyphen
  return date.toLocaleDateString("en-US", options).replace(" ", "-");
};

export const getFiscalYear = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth(); // 0 for January, 11 for December

  if (currentMonth >= 3) {
    return `FY ${currentYear % 100}`;
  } else {
    return `FY ${(currentYear - 1) % 100}`;
  }
};

export const newShade = (hexColor, magnitude) => {
  const convertedColor = convertColorFormat(hexColor, "hex");
  let hexColorChange = convertedColor.replace(`#`, ``);

  if (hexColorChange.length === 6) {
    const decimalColor = parseInt(hexColorChange, 16);

    let r = (decimalColor >> 16) + magnitude;
    let g = (decimalColor & 0x0000ff) + magnitude;
    let b = ((decimalColor >> 8) & 0x00ff) + magnitude;

    // Clamp RGB values to ensure they stay within the 0–255 range
    r = Math.min(255, Math.max(0, r));
    g = Math.min(255, Math.max(0, g));
    b = Math.min(255, Math.max(0, b));

    // Add bias towards white by ensuring higher values for R, G, B
    const whiteBias = 30; // Increase this value to further emphasize white
    r = Math.min(255, r + whiteBias);
    g = Math.min(255, g + whiteBias);
    b = Math.min(255, b + whiteBias);

    return `#${((1 << 24) + (r << 16) + (b << 8) + g)
      .toString(16)
      .slice(1)
      .toUpperCase()}`;
  } else {
    return hexColorChange;
  }
};

export const formatAsDollarYTD = (value) => {
  const formatAscost = (value) => {
    if (value >= 1000000000) {
      return (value / 1000000000).toFixed(1) + "B";
    } else if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + "M";
    } else {
      return parseInt(value).toFixed(2); //formatted as a number
    }
  };
  const cost = formatAscost(value);
  // If cost is already formatted as 'B', 'M', or 'K', skip additional formatting
  if (typeof cost === "string" && /[BMK]$/.test(cost)) {
    return `S$${cost}`;
  }
  const formattedValue = new Intl.NumberFormat("en-SG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

  return `S$${formattedValue}`;
};

export const convertEpochToISO8601 = (epochMillis) => {
  const date = new Date(epochMillis); // convert milliseconds to a Date object
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const seconds = String(date.getUTCSeconds()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000+00:00`;
};

export function graphEpochToDate(epoch) {
  const date = new Date(epoch * 1000); // Convert epoch to milliseconds
  const options = { year: "numeric", month: "short", day: "numeric" };
  const formattedDate = date.toLocaleDateString(undefined, options);
  return formattedDate;
}

export function getMonnthyearasNum(month) {
  //June 2024  to  2024-6
  const [monthName, year] = month.split(" ");
  const monthNumber = getMonthNumber(monthName);
  const formattedMonth = `${monthNumber}/${year}`;
  return formattedMonth;
}

export const getSelectedOptionsValue = (options = []) => {
  return map(options, "value");
};
export const getSelectedOptionsById = (options = []) => {
  const stringArray = map(options, "id");
  return stringArray.map((item) => item.toString());
  // return map(options, "id")
};
export const filterOptions = (optionsProps, searchText, setState) => {
  setState(
    optionsProps?.filter((option) => option.label?.includes(searchText))
  );
};
export const getPercentage = (value, total) => {
  let percentVal = 0;
  if (value !== 0 && total !== 0) {
    percentVal = (value / total) * 100;
    percentVal = percentVal.toFixed(2);
  }
  return percentVal;
};
export const truncate = (input, leng) => {
  if (input.length > leng) {
    return input.substring(0, leng) + "...";
  }
  return input;
};

export const responsivefy = (svg) => {
  // container will be the DOM element
  // that the svg is appended to
  // we then measure the container
  // and find its aspect ratio
  const container = d3.select(svg.node().parentNode),
    width = parseInt(svg.style("width"), 10),
    height = parseInt(svg.style("height"), 10),
    aspect = width / height;

  var doit;
  // set viewBox attribute to the initial size
  // control scaling with preserveAspectRatio
  // resize svg on inital page load
  svg
    .attr("viewBox", `0 0 ${width} ${height}`)
    .attr("preserveAspectRatio", "xMinYMid")
    .call(resize);

  // add a listener so the chart will be resized
  // when the window resizes
  // multiple listeners for the same event type
  // requires a namespace, i.e., 'click.foo'
  // api docs: https://goo.gl/F3ZCFr
  d3.select(window).on("resize." + container.attr("id"), resize);

  // this is the code that resizes the chart
  // it will be called on load
  // and in response to window resizes
  // gets the width of the container
  // and resizes the svg to fill it
  // while maintaining a consistent aspect ratio

  function resizedw() {
    const w = parseInt(container.style("width"));
    svg.attr("width", w);
    svg.attr("height", Math.round(w / aspect));
  }

  function resize() {
    clearTimeout(doit);
    doit = setTimeout(resizedw, 200);
  }
};
export function logOut() {
  window.location.href = "/auth";
  sessionStorage.setItem("isLoggedin", false);
  sessionStorage.setItem("lastVistedURL", window.location.pathname);
  cookies.remove("username", { path: "/" });
  cookies.remove("apitoken", { path: "/" });
  cookies.remove("jwttoken", { path: "/" });
  cookies.remove("Refersh", { path: "/" });
  cookies.remove("referstoken", { path: "/" });
}

export const handleResponseCode = (response) => {
  try {
    if (response?.status === 403) {
      logOut();
    } else if (response?.redirected) {
      logOut();
    } else if (response?.status === 401) {
      logOut();
    }

    return response;
  } catch (error) {
    console.error("An error occurred while handling the response:", error);
  }
};

export const resetLocalStorage = () => {
  const currentLocationBeforeDecode = window.location.href;
  const currentLocation = decodeURIComponent(
    currentLocationBeforeDecode
  ).replaceAll(" ", "");
  const redirectedUrl = localStorage.getItem("redirectURL");

  if (redirectedUrl) {
    const redirectedUrlPath = redirectedUrl.split("#")[0];

    if (
      currentLocation == redirectedUrl ||
      currentLocation == redirectedUrlPath
    ) {
      localStorage.setItem("redirectURL", "");
    }
  }
};

export const severityFiltersMap = (Severity) => {
  if (Array.isArray(Severity)) {
    const severityFilters = Severity.map((severity, index) => ({
      label: severity,
      value: severity,
      id: index + 1,
    }));
    return severityFilters;
  }
};
export const setRedirectPath = () => {
  const actualUrlBeforeDecode = window.location.href;
  const actualUrl = decodeURIComponent(actualUrlBeforeDecode).replaceAll(
    " ",
    ""
  );
  let urlArray = actualUrl.split("#");
  let urlToRedirect = urlArray[0];
  if (actualUrl.includes("redirectedFromEmail")) {
    const allUrl = urlArray[1] && urlArray[1].split("&");
    let appName = allUrl ? allUrl[1] || "" : "";
    const appCode = appName.split("=")[1];
    const refId = allUrl[2] ? allUrl[2].split("=")[1] : "";
    localStorage.setItem("redirectURlAppNames", appCode);
    localStorage.setItem("redirectURL", actualUrl);
    localStorage.setItem("refIdForSelectedVulnerabilites", refId);
  }
};

function convertEpochTimeToMilliseconds(epochTime) {
  if (epochTime > 1e12) {
    return epochTime;
  } else {
    const epochTimeInMilliseconds = epochTime * 1000;
    return epochTimeInMilliseconds;
  }
}

export const convertEpochToDate = (givenDate) => {
  if (givenDate === "null" || givenDate === 0) {
    return "-";
  }

  // Convert epoch to DateTime in server's timezone
  const dateTime = DateTime.fromMillis(
    convertEpochTimeToMilliseconds(givenDate),
    { zone: serverZone }
  );

  // Format components
  const day = dateTime.toFormat("dd");
  const month = dateTime.toFormat("MM");
  const year = dateTime.toFormat("yyyy");
  const hours = dateTime.toFormat("HH");
  const minutes = dateTime.toFormat("mm");

  return `${day}/${month}/${year}, ${hours}:${minutes}`;
};

export const convertEpochToDate_withouttime = (epoch) => {
  const date = new Date(epoch);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export function convertToEpoch(dateString) {
  const epochTime = Date.parse(dateString);
  return epochTime / 1000; // Convert milliseconds to seconds
}

export function formatDateYear(givenDate) {
  if (givenDate === "null") {
    return "-";
  }

  const dateObj = new Date(convertEpochTimeToMilliseconds(givenDate));

  const day = dateObj.toLocaleString("en-US", { day: "2-digit" });
  const month = dateObj.toLocaleString("en-US", { month: "2-digit" });
  const year = dateObj.toLocaleString("en-US", { year: "numeric" });

  return `${day}/${month}/${year}`;
}

export const isEmptyObject = (object) => {
  return (
    object && Object.keys(object).length === 0 && object.constructor === Object
  );
};

export const capitalizeFirstLetter = (string) => {
  if (typeof string !== "string" || string.length === 0) {
    return string; // Return the input as is
  }
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export const removeNullEntries = (data) => {
  const cleanedData = {};

  for (const key in data) {
    if (data[key] !== null) {
      cleanedData[key] = data[key];
    }
  }

  return cleanedData;
};

export const encrypt = (data) => {
  const SECRET = "DevopsLabs2024";
  const key = CryptoJS.enc.Utf8.parse("En" + SECRET);
  const encrypted = CryptoJS.AES.encrypt(data, key, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  }).toString();

  const urlSafeEncrypted = encrypted
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/={1,100}$/, "");

  return urlSafeEncrypted;
};

export const decrypt = (encryptedData) => {
  try {
    const key = "DevopsLabs2024";
    const dataToDecrypt = encryptedData.slice(4, -3);

    const secretKeyString = "En" + key;

    const keyBytes = CryptoJS.enc.Utf8.parse(secretKeyString);

    const base64Encrypted = dataToDecrypt
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(
        dataToDecrypt.length + ((4 - (dataToDecrypt.length % 4)) % 4),
        "="
      );

    const encryptedBytes = CryptoJS.enc.Base64.parse(base64Encrypted);

    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: encryptedBytes },
      keyBytes,
      {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7,
      }
    );

    const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
    return decryptedText;
  } catch (error) {
    console.error("Decryption error:", error);
    return null;
  }
};

//Common Alert
export const useCommonAlert = () => {
  // this is the way to call the function
  // callCommonAlert("error","Something went wrong. Please try again.");
  // hideCommonAlert();
  //type =success,warning,error

  const dispatch = useDispatch();
  const alertTimeoutRef = useRef(null);
  const navigateClearRef = useRef(true);

  const callCommonAlert = (type, msg, clearNavigate) => {
    navigateClearRef.current = clearNavigate;
    if (alertTimeoutRef.current) {
      clearTimeout(alertTimeoutRef.current);
    }
    dispatch(
      setCommonAlert({
        openAlert: true,
        alertType: type,
        alertMessage: msg,
        clearNavigate, // give true when alertpopup to show when navigate(unmounts)
      })
    );
  };

  const hideCommonAlert = (duration) => {
    // 🔄 Clear any existing timers
    if (alertTimeoutRef.current) {
      clearTimeout(alertTimeoutRef.current);
      alertTimeoutRef.current = null;
      console.log("🧹 Cleared previous timer");
    }

    const delay =
      typeof duration === "number" && duration >= 0 ? duration : 3000;

    alertTimeoutRef.current = setTimeout(() => {
      dispatch(
        setCommonAlert({
          openAlert: false,
          alertType: "",
          alertMessage: "",
        })
      );
      alertTimeoutRef.current = null;
    }, delay);
  };

  useEffect(() => {
    // prevent unwanted popup alert when navigting(unmount) through pages
    return () => {
      if (alertTimeoutRef.current && navigateClearRef.current) {
        clearTimeout(alertTimeoutRef.current);
      }
      if (!navigateClearRef.current) {
        dispatch(
          setCommonAlert({
            openAlert: false,
            alertType: "",
            alertMessage: "",
          })
        );
      }
    };
  }, []);
  return { callCommonAlert, hideCommonAlert };
};
export const encryptData = (data) => {
  const SECRET = "DevopsLabs2024";
  const key = CryptoJS.enc.Utf8.parse("En" + SECRET);
  const encrypted = CryptoJS.AES.encrypt(data, key, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  }).toString();

  const urlSafeEncrypted = encrypted
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/={1,100}$/, "");
  return urlSafeEncrypted;
};
// Decryption Function
export const decryptData = (encryptedData) => {
  const SECRET = "DevopsLabs2024";
  const key = CryptoJS.enc.Utf8.parse("En" + SECRET);
  let base64String = encryptedData
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(
      encryptedData.length +
      (encryptedData.length % 4 === 0 ? 0 : 4 - (encryptedData.length % 4)),
      "="
    );
  const decrypted = CryptoJS.AES.decrypt(base64String, key, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  });
  return decrypted.toString(CryptoJS.enc.Utf8);
};

// --- AES-GCM async helpers (Web Crypto API) ---
// Match the backend JwtUtils GCM wire format:
//   Base64Url( [GCM1 magic 4 bytes][IV 12 bytes][ciphertext + 16-byte auth tag] )

const GCM_MAGIC = new Uint8Array([0x47, 0x43, 0x4d, 0x31]); // "GCM1"
const GCM_IV_SIZE = 12;
const ENCRYPTION_SECRET = "DevopsLabs2024";

function base64UrlDecode(str) {
  let b64 = str.replace(/-/g, "+").replace(/_/g, "/");
  while (b64.length % 4 !== 0) b64 += "=";
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function base64UrlEncode(bytes) {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function getGcmKey() {
  const rawKey = new TextEncoder().encode("En" + ENCRYPTION_SECRET);
  return crypto.subtle.importKey("raw", rawKey, "AES-GCM", false, [
    "encrypt",
    "decrypt",
  ]);
}

function hasGcmMagic(blob) {
  if (blob.length < GCM_MAGIC.length) return false;
  for (let i = 0; i < GCM_MAGIC.length; i++) {
    if (blob[i] !== GCM_MAGIC[i]) return false;
  }
  return true;
}

export async function decryptDataAsync(encryptedData) {
  try {
    if (!encryptedData || typeof encryptedData !== "string") return "";
    const blob = base64UrlDecode(encryptedData);

    if (hasGcmMagic(blob)) {
      const iv = blob.slice(GCM_MAGIC.length, GCM_MAGIC.length + GCM_IV_SIZE);
      const ciphertext = blob.slice(GCM_MAGIC.length + GCM_IV_SIZE);
      const key = await getGcmKey();
      const decrypted = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv },
        key,
        ciphertext,
      );
      return new TextDecoder().decode(decrypted);
    } else {
      return decryptData(encryptedData);
    }
  } catch (error) {
    console.error("decryptDataAsync error:", error);
    return "";
  }
}

export async function encryptDataAsync(data) {
  try {
    const key = await getGcmKey();
    const iv = crypto.getRandomValues(new Uint8Array(GCM_IV_SIZE));
    const encoded = new TextEncoder().encode(data);
    const encrypted = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      encoded,
    );
    const encryptedBytes = new Uint8Array(encrypted);

    const out = new Uint8Array(
      GCM_MAGIC.length + GCM_IV_SIZE + encryptedBytes.length,
    );
    out.set(GCM_MAGIC, 0);
    out.set(iv, GCM_MAGIC.length);
    out.set(encryptedBytes, GCM_MAGIC.length + GCM_IV_SIZE);

    return base64UrlEncode(out);
  } catch (error) {
    console.error("encryptDataAsync error:", error);
    return null;
  }
}

export const cvIdClickValidation = (CAT, data, msg) => {
  const targetDate = convertToEpoch(get(data, "targetDate"));
  const cTodaysDate = new Date();
  const shouldHighlight =
    data.status === "Pending Approval"
      ? data.status === "Pending Approval" &&
      convertToEpoch(cTodaysDate) > targetDate
      : get(data, "targetDate") === "null"
        ? true
        : convertToEpoch(cTodaysDate) > targetDate;
  const validateStatusOpen =
    data.status === "Open"
      ? true
      : data.status === "Pending Approval"
        ? shouldHighlight
        : false;
  let message;
  if (CAT === data.criticality && validateStatusOpen && shouldHighlight) {
    return true;
  } else {
    let errors = [];

    if (CAT !== data.criticality) {
      errors.push("Criticality");
    }
    if (!validateStatusOpen) {
      errors.push("Status");
    }
    if (!shouldHighlight) {
      errors.push("Target date");
    }

    let message = errors.length
      ? `${errors.join(" and ")} ${errors.length > 1 ? "are" : "is"} not valid`
      : "";

    return msg ? message : false;
  }
};

export const getMonthYearRange = (startTimestamp, endTimestamp) => {
  const startDate = new Date(startTimestamp * 1000);
  const endDate = new Date(endTimestamp * 1000);

  const result = [];
  const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  // eslint-disable-next-line no-unmodified-loop-condition
  while (current <= endDate) {
    const month = current.toLocaleString("default", { month: "short" });
    const year = current.getFullYear();
    const label = `${month} ${year}`;
    result.push(label);
    current.setMonth(current.getMonth() + 1);
  }

  return result;
};

export const getMonthStartEndEpoch = (label) => {
  // month Jan 2025 will give the start end enddate in epoch
  const [monthStr, yearStr] = label.split(" ");
  const monthIndex = new Date(`${monthStr} 1, 2000`).getMonth();
  const year = parseInt(yearStr, 10);

  const startDate = new Date(year, monthIndex, 1);
  const today = new Date();

  const isCurrentMonth =
    today.getFullYear() === year && today.getMonth() === monthIndex;

  const endDate = isCurrentMonth ? today : new Date(year, monthIndex + 1, 0);

  return {
    startEpoch: Math.floor(startDate.getTime() / 1000),
    endEpoch: Math.floor(endDate.getTime() / 1000),
  };
};

export const userRoleExtract = (userRole) => {
  if (userRole && userRole.length !== 0) {
    return userRole[0];
  }
  return null;
};

// to conver data in dd/mmm/yyyy  * all format will accept
export function standardDateFormat(input) {
  const formatsToTry = [
    "DD-MM-YYYY",
    "DD/MM/YYYY",
    "DD.MM.YYYY",
    "DD MM YYYY",
    "DD-MM-YY",
    "DD/MM/YY",
    "DD.MM.YY",
    "DD MM YY",
    "YYYY-MM-DD",
    "YYYY/MM/DD",
    "YYYY.MM.DD",
    "YYYY MM DD",
    "YY-MM-DD",
    "YY/MM/DD",
    "YY.MM.DD",
    "YY MM DD",
    "MM-DD-YYYY",
    "MM/DD/YYYY",
    "MM.DD.YYYY",
    "MM DD YYYY",
    "DD-MMM-YYYY",
    "DD/MMM/YYYY",
    "DD MMM YYYY",
    "DD-MMM-YY",
    "DD/MMM/YY",
    "DD MMM YY",
    "MMM DD YYYY",
    "MMM-DD-YYYY",
    "MMM/DD/YYYY",
    "MMMM DD YYYY",
    "DD MMMM YYYY",
    "MMMM-DD-YYYY",
  ];

  const preprocess = (str) =>
    str
      .replace(/[^a-zA-Z0-9]/g, " ") // Replace all separators with space
      .replace(/\s+/g, " ") // Collapse multiple spaces
      .trim();

  const formatDate = (dateStr) => {
    const cleaned = preprocess(dateStr);
    const parsed = moment(cleaned, formatsToTry, true);

    if (!parsed.isValid()) {
      return "Invalid date format";
    }

    return parsed.format("DD/MM/YYYY");
  };

  if (Array.isArray(input)) {
    return input.map(formatDate);
  }

  return formatDate(input);
}

export const DDAllHide = () => {
  const dispatch = useDispatch();
  dispatch(hideOffCanvas(false));
  dispatch(hideOffCanvasThree(false));
  dispatch(hideOffCanvasLast(false));
};

// Formats a date string to "Mon DD, YYYY, HH:MM AM/PM" (en-US locale with time)
export function formatDateWithTime(dateString) {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// label percentage format
export const formatPercentage = (val) => {
  const rounded = Math.round(val * 10) / 10;
  return rounded % 1 === 0
    ? `${Math.round(rounded)}%`
    : `${rounded.toFixed(1)}%`;
};

//// markpoint value formatter
export const formatMarkPoint = (value, isPercent = false) => {
  if (isPercent) {
    return formatPercentage(value);
  }

  const format = (val, suffix) => {
    return `${val.toFixed(1).replace(/\.0$/, "")}${suffix}`;
  };

  if (value >= 1_000_000_000_000) {
    return format(value / 1_000_000_000_000, "T");
  }
  if (value >= 1_000_000_000) {
    return format(value / 1_000_000_000, "B");
  }
  if (value >= 1_000_000) {
    return format(value / 1_000_000, "M");
  }
  if (value >= 1_000) {
    return `${Math.floor(value / 1_000)}K`;
  }
  return `${Math.floor(value)}`;
};

// sanitize data before use
export const sanitizeData = (data) => {
  if (Array.isArray(data)) {
    return data.filter((v) => v !== null && v !== undefined);
  }

  if (data && typeof data === "object") {
    return Object.keys(data).reduce((acc, key) => {
      acc[key] = sanitizeData(data[key]);
      return acc;
    }, {});
  }

  if (typeof data === "number") {
    return Number.isFinite(data) ? data : 0;
  }
  if (typeof data === "boolean") {
    return data;
  }
  if (typeof data === "string") {
    return data.trim();
  }

  return null;
};

/**
 * Safely removes a child element from parent
 * Prevents "Failed to execute 'removeChild'" errors
 *
 * @param {HTMLElement} parent - Parent element
 * @param {HTMLElement} child - Child element to remove
 * @returns {boolean} Success status
 *
 * @example
 * const parent = document.getElementById('container');
 * const child = document.getElementById('item');
 * safeRemoveChild(parent, child);
 */
export const safeRemoveChild = (parent, child) => {
  if (!parent || !child) {
    return false;
  }

  try {
    // Verify child is actually a child of parent before removing
    if (parent.contains(child)) {
      parent.removeChild(child);
      return true;
    }
  } catch (error) {
    console.warn('Failed to remove child element:', error);
  }

  return false;
};

/**
 * Safely adds event listener with automatic cleanup tracking
 * Returns cleanup function for use in useEffect
 *
 * @param {HTMLElement} element - Element to attach listener to
 * @param {string} event - Event name
 * @param {Function} handler - Event handler function
 * @param {Object} options - Event listener options (optional)
 * @returns {Function} Cleanup function that removes the event listener
 *
 * @example
 * useEffect(() => {
 *   const cleanup = safeAddEventListener(
 *     window,
 *     'resize',
 *     handleResize
 *   );
 *   return cleanup;
 * }, []);
 */
export const safeAddEventListener = (element, event, handler, options) => {
  if (!element || !event || !handler) {
    return () => { };
  }

  element.addEventListener(event, handler, options);

  return () => {
    element.removeEventListener(event, handler, options);
  };
};

export const calculatePageCount = (totalCount, pageLimit) => {
  const calculatedPageCount = totalCount ? Math.ceil(totalCount / pageLimit) : 0;
  const finalPageCount = calculatedPageCount !== 0 ? calculatedPageCount : 1;
  return finalPageCount;
};